import React from 'react';
import type { ConversationDto } from '../../services/api/messageService';
import { ProfileCardTrigger } from '../profile/ProfileCardTrigger';
import './ConversationList.css';

interface ConversationListProps {
  conversations: ConversationDto[];
  selectedMatchId?: string;
  onSelectConversation: (matchId: string) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedMatchId,
  onSelectConversation,
}) => {
  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const truncateMessage = (content: string, maxLength: number = 50) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength - 3) + '...';
  };

  if (conversations.length === 0) {
    return (
      <div className="conversation-list-empty">
        <div className="empty-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
          </svg>
        </div>
        <h3>No conversations yet</h3>
        <p>When you match with someone, you can start chatting here!</p>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      {conversations.map((conversation) => (
        <div
          key={conversation.matchId}
          className={`conversation-item ${
            selectedMatchId === conversation.matchId ? 'selected' : ''
          } ${conversation.unreadCount > 0 ? 'unread' : ''}`}
          onClick={() => onSelectConversation(conversation.matchId)}
        >
          <ProfileCardTrigger profileId={conversation.otherProfileId}>
            <div className="conversation-avatar">
              {conversation.otherPhotoUrl ? (
                <img src={conversation.otherPhotoUrl} alt={conversation.otherDisplayName} />
              ) : (
                <div className="avatar-placeholder">
                  {conversation.otherDisplayName.charAt(0).toUpperCase()}
                </div>
              )}
              {conversation.otherIsVerified && (
                <div className="verified-badge" title="Verified">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </div>
              )}
            </div>
          </ProfileCardTrigger>

          <div className="conversation-content">
            <div className="conversation-header">
              <span className="conversation-name">
                {conversation.otherDisplayName}
                {conversation.isSuperLikeMatch && (
                  <span className="superlike-indicator" title="Super Like match">★</span>
                )}
              </span>
              <span className="conversation-time">
                {formatTime(conversation.lastMessageAt || conversation.matchedAt)}
              </span>
            </div>
            <div className="conversation-preview">
              {conversation.lastMessage ? (
                <span className={conversation.lastMessage.isOwnMessage ? 'own-message' : ''}>
                  {conversation.lastMessage.isOwnMessage && 'You: '}
                  {conversation.lastMessage.type === 'image' ? '📷 Photo' :
                   conversation.lastMessage.type === 'gif' ? '🎬 GIF' :
                   truncateMessage(conversation.lastMessage.content)}
                </span>
              ) : (
                <span className="new-match">New match! Say hello 👋</span>
              )}
            </div>
          </div>

          {conversation.unreadCount > 0 && (
            <div className="unread-badge">
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ConversationList;
